import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { List } from 'immutable';
import { getFilters, getTimePeriodFilter } from '../selectors/filtersSelectors';
import { getDealGroupingTypeFilter } from '../selectors/filtersSelectors';
import { getSelectedTeamId } from '../selectors/overviewSelectors';
import { getIsAllPipelines, getPortalWideCadence, getPipelines, getSelectedForecastType } from '../selectors/forecastSelectors';
import { FILTERS } from '../constants/FiltersConstants';
import { DEAL_STAGE } from '../constants/DealGroupingType';
import { CLOSED_WON, FORECAST_SUBMISSION, shouldFetchChangeIndicators, WEEK_OVER_WEEK } from '../constants/ChangeIndicators';
import { getTimePeriodOffset, getTimePeriodOption, isTimePeriodCustom } from '../utils/dateUtils';
import { transformChangeValuesFromResponse } from '../utils/apiUtils';
import { fetchDealRollupsOverTime, fetchSubmissionChangeOverTime } from '../api/forecastApi';
import { getCompanyCurrency } from '../selectors/multiCurrencySelectors';
import { getIsUngatedToForecastingMultipleOutcomes } from '../selectors/authSelectors';
import { getCurrentDatePortalTz } from '../selectors/dateSelectors';
const buildRequestData = state => {
  var _getSelectedForecastT;
  const selectedTeamId = getSelectedTeamId(state);
  const isDealStage = getDealGroupingTypeFilter(state) === DEAL_STAGE;
  const filters = getFilters(state);
  const timePeriod = getTimePeriodFilter(state);
  const pipelines = getPipelines(state);
  const cadence = getPortalWideCadence(state);
  const isAllPipelines = getIsAllPipelines(state);
  const localDateNow = getCurrentDatePortalTz(state);
  let pipeline;
  if (!pipelines) {
    pipeline = List();
  } else {
    const pipelineIds = pipelines.map(pipelineResult => pipelineResult.get('pipelineId')).toSet();

    // filters existing pipeline ids
    pipeline = filters.get(FILTERS.PIPELINE).toSet().intersect(pipelineIds).toList();

    // if no valid pipeline ids in filters, use first pipeline id and corresponding cadence
    if (!pipeline.size) {
      const pipelineId = pipelineIds.first();
      pipeline = List([pipelineId]);
    }
  }
  const timePeriodOption = getTimePeriodOption(timePeriod);
  const timePeriodOffset = getTimePeriodOffset(timePeriod);
  const isCustomPeriod = isTimePeriodCustom(timePeriod);
  const customStartDate = isCustomPeriod ? timePeriod.get('startDate') : null;
  const customEndDate = isCustomPeriod ? timePeriod.get('endDate') : null;
  const isFmoEnabled = getIsUngatedToForecastingMultipleOutcomes(state);
  const selectedForecastTypeId = isFmoEnabled ? (_getSelectedForecastT = getSelectedForecastType(state)) === null || _getSelectedForecastT === void 0 ? void 0 : _getSelectedForecastT.forecastTypeId : undefined;
  return Object.assign({
    pipeline: pipeline.toArray(),
    pipelineCadence: cadence,
    timePeriod: {
      timePeriodOption,
      timePeriodOffset,
      customStartDate,
      customEndDate
    },
    selectedTeamId,
    isAllPipelines,
    isTeamView: !!selectedTeamId,
    isGroupedByStage: isDealStage,
    overTimeFrequency: 'WEEK',
    localDateNow
  }, isFmoEnabled ? {
    forecastTypeId: selectedForecastTypeId
  } : {});
};
const useFetchDataWellsChange = (isDataFetching, currentSubmission) => {
  const state = useSelector(entireState => entireState);
  const companyCurrency = useSelector(getCompanyCurrency);
  const data = buildRequestData(state);
  const [closedWonResponseLoading, setClosedWonResponseLoading] = useState(false);
  const [submissionResponseLoading, setSubmissionResponseLoading] = useState(false);
  const DEFAULT_CHANGE_VALUES = {
    currencyCode: '',
    change: 0,
    currentAmount: 0,
    isTooEarlyInPeriod: false,
    percentChange: 0,
    previousAmount: 0
  };
  const [closedWonChange, setClosedWonChange] = useState(DEFAULT_CHANGE_VALUES);
  const [submissionChange, setSubmissionChange] = useState(DEFAULT_CHANGE_VALUES);
  const timePeriod = getFilters(state).get(FILTERS.TIME_PERIOD);
  const shouldFetchClosedWonChange = shouldFetchChangeIndicators({
    dataWell: CLOSED_WON,
    comparisonFilter: WEEK_OVER_WEEK,
    timePeriod
  });
  const shouldFetchSubmissionChange = shouldFetchChangeIndicators({
    dataWell: FORECAST_SUBMISSION,
    comparisonFilter: WEEK_OVER_WEEK,
    timePeriod
  });
  useEffect(() => {
    if (!isDataFetching && shouldFetchClosedWonChange) {
      setClosedWonResponseLoading(true);
      fetchDealRollupsOverTime(data).then(response => {
        const aggregateClosedWonChangeTime = response.aggregatedClosedWonChange;
        // Keep the default values if the response is null
        if (!aggregateClosedWonChangeTime) {
          return;
        }
        setClosedWonChange(transformChangeValuesFromResponse({
          companyCurrency,
          response,
          rollUp: aggregateClosedWonChangeTime
        }));
      }).catch(() => {});
      setClosedWonResponseLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataFetching]);
  useEffect(() => {
    if (!isDataFetching && shouldFetchSubmissionChange) {
      setSubmissionResponseLoading(true);
      fetchSubmissionChangeOverTime(data).then(response => {
        const aggregatedForecastOverTime = response.aggregatedForecastOverTime;
        // Keep the default values if the response is null
        if (!aggregatedForecastOverTime) {
          return;
        }
        setSubmissionChange(transformChangeValuesFromResponse({
          companyCurrency,
          response,
          rollUp: aggregatedForecastOverTime
        }));
      }).catch(() => {});
      setSubmissionResponseLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSubmission, isDataFetching]);
  return {
    changeIndicatorsLoading: submissionResponseLoading || closedWonResponseLoading,
    closedWonChange,
    submissionChange
  };
};
export default useFetchDataWellsChange;